import { UploadOutlined } from '@ant-design/icons';
import { Upload } from 'antd';
import { UploadProps } from 'antd/lib/upload';
import { RcFile, UploadChangeParam, UploadFile, UploadFileStatus } from 'antd/lib/upload/interface';
import React, { useContext } from 'react';

import Paragraph from 'components/atoms/Paragraph';
import { Context } from 'components/hoc/Store';

import apiPaths from 'enums/apiPaths';
import { ENotificationType } from 'enums/notificationType';
import { globalInputRegExp } from 'enums/regExp';

import ButtonComponent from './ButtonComponent';

export interface IFileList {
    uid: string;
    oid?: number;
    name: string;
    fileName: string; // nastavit na temp nazev ktory vracia upload
    type: string;
    size: number;
    status: UploadFileStatus;
}

interface IUpload extends UploadProps {
    text?: string;
    buttonLoading?: boolean; // loading napr pri downloade alebo validacii, pri uploade je loading defaultne
    buttonDisabled?: boolean;
    defaultFileList?: IFileList[] | UploadFile[]; // Default list of files that have been uploaded
    fileList?: IFileList[]; // List of files that have been uploaded (controlled), pouzite pri extra funkcionalite ako max pocet suborov..
    disabled?: boolean;
    showDownloadIcon?: boolean;
    showRemoveIcon?: boolean;
    extramessage?: string;
    accept?: string;
    onChange?: (data: UploadChangeParam) => void; // prebehne 3x https://ant.design/components/upload/#API
    onRemove?: (file: UploadFile) => void;
    onDownload?: (file: UploadFile) => void;
    onButtonClick?: () => void;
    beforeUpload?: (file: RcFile, fileList: RcFile[]) => boolean | Promise<void>;
    hideButton?: boolean;
    buttonId?: string;
    uploadPath?: string;
}

const UploadComponent: React.FC<IUpload> = (props: IUpload) => {
    const { token, NotificationComponent } = useContext(Context);
    const {
        text = 'VYBRAT_SOUBOR',
        accept = '.pdf, .rtf, .docx, .doc, .jpg, .png, .gif, .jpeg, .xml',
        buttonLoading,
        buttonDisabled,
        buttonId,
        defaultFileList,
        fileList,
        disabled,
        extramessage,
        showDownloadIcon = true,
        showRemoveIcon = true,
        hideButton = false,
        onChange,
        onRemove,
        onDownload,
        onButtonClick,
        beforeUpload = file => {
            const type = file.name.split('.').pop();
            const name = file.name.split('.').slice(0, -1).join('.');
            // kontrola formatu
            if (type && !accept.includes(type.toLocaleLowerCase())) {
                NotificationComponent({
                    description: 'UPLOAD_INVALID_FORMAT',
                    type: ENotificationType.ERROR
                });
                return false;
            }
            // kontrola dlzky nazvu a nepovolenych znakov
            if (name && !globalInputRegExp.test(name) && new TextEncoder().encode(name).length > 512) {
                NotificationComponent({
                    description: 'UPLOAD_INVALID_NAME',
                    type: ENotificationType.ERROR
                });
                return false;
            }
            // kontrola maximalnej velkosti s suboru
            if (file.size / 1024 / 1024 > 20) {
                NotificationComponent({
                    title: 'UPLOAD_MAX_SIZE',
                    description: 'UPLOAD_MAX_SIZE_TEXT',
                    type: ENotificationType.ERROR
                });
                return false;
            }
            return true;
        },
        uploadPath,
        ...rest
    } = props;
    return (
        <>
            <Upload
                style={{
                    width: 300,
                    display: 'block'
                }}
                accept={accept}
                headers={{
                    authorization: `Bearer ${token}` || ''
                }}
                // eslint-disable-next-line no-undef
                action={
                    window.location.hostname.includes('localhost')
                        ? process.env.REACT_APP_ISB_URL + (uploadPath ? '/' + uploadPath : apiPaths.FILE_UPLOAD)
                        : `https://${window.location.hostname}/isbdepositjson/api` +
                          (uploadPath ? '/' + uploadPath : apiPaths.FILE_UPLOAD)
                } // Uploading URL
                data={(file: UploadFile) => {
                    return {
                        nazev: file.name
                    };
                }} // extra data poslane v requeste
                defaultFileList={defaultFileList}
                fileList={fileList}
                disabled={disabled}
                showUploadList={{
                    showRemoveIcon: showRemoveIcon,
                    showDownloadIcon: showDownloadIcon
                }}
                beforeUpload={beforeUpload}
                onChange={onChange}
                onRemove={onRemove}
                onDownload={onDownload}
                {...rest}
            >
                {!hideButton && (
                    <>
                        <ButtonComponent
                            id={buttonId}
                            icon={<UploadOutlined />}
                            loading={buttonLoading}
                            disabled={buttonDisabled || disabled}
                            onClick={onButtonClick}
                            htmlType="button"
                            type="default"
                            buttontext={text}
                        />
                        <div>{extramessage && <Paragraph text={extramessage} className="extramessage-text" />}</div>
                    </>
                )}
            </Upload>
        </>
    );
};

export default UploadComponent;
